import { FordeerApp } from '@fordeer/ui-components'
import { postHogErrorTracking } from '@services/posthogErrorTracking'
import { forceFullPageReload } from '@utils/forceFullPageReload'
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error boundary caught an error:', error, errorInfo)

    postHogErrorTracking.captureException(error, {
      feature: FordeerApp.Invoice,
      source: 'error-boundary',
      severity: 'error',
      componentStack: errorInfo.componentStack ?? undefined,
    })

    this.setState({
      error,
      errorInfo,
    })
  }

  private handleReload = () => {
    // Hard full-document reload — App Bridge soft nav often cannot recover
    // from chunk/deploy mismatches or a stuck error UI.
    forceFullPageReload()
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Simple error UI without Polaris components to avoid context issues
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f6f6f7',
            padding: '2rem',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxWidth: '500px',
              width: '100%',
            }}
          >
            <h2 style={{ color: '#bf0711', marginTop: 0 }}>
              Something went wrong
            </h2>
            <p style={{ color: '#616161', marginBottom: '1.5rem' }}>
              The application encountered an unexpected error. You can try
              reloading the page or resetting the error state.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details style={{ marginBottom: '1.5rem' }}>
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                  Error details (development only)
                </summary>
                <pre
                  style={{
                    marginTop: '0.5rem',
                    padding: '1rem',
                    backgroundColor: '#f6f6f6',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '200px',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  backgroundColor: '#008060',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Reload Page
              </button>
              <button
                type="button"
                onClick={this.handleReset}
                style={{
                  backgroundColor: '#f6f6f7',
                  color: '#212b36',
                  border: '1px solid #c4cdd5',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
